import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { fmtOdds } from "../lib/api";
import { cashTier as tier, BetDetail } from "./PropsThatPay";
import { ChevronDown } from "lucide-react";

const MARKETS = [
  { key: "ml", label: "Moneylines" },
  { key: "spread", label: "Spreads" },
  { key: "total", label: "Totals" },
];
const H1_LEAGUES = ["nfl", "ncaaf", "nba"];

export default function LikelyCash({ league = "mlb", comp, standalone = false }) {
  const [mk, setMk] = useState("ml");
  const [open, setOpen] = useState(null);
  const hasH1 = H1_LEAGUES.includes(league);
  const isH1 = mk === "h1";
  const { data, isLoading } = useQuery({
    queryKey: ["likelyCash", league, comp],
    queryFn: () => api.get(`/likely-cash?sport=${league}${comp ? `&comp=${comp}` : ""}`).then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  });
  const h1q = useQuery({
    queryKey: ["firstHalf", league],
    queryFn: () => api.get(`/first-half?league=${league}`).then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    enabled: hasH1 && isH1,
  });
  const tabs = hasH1 ? [...MARKETS, { key: "h1", label: "1st Half" }] : MARKETS;
  const bets = isH1 ? h1q.data?.bets || [] : data?.markets?.[mk] || [];
  const loading = isH1 ? h1q.isLoading : isLoading;
  const rec = data?.record;
  const fmtRec = (r) => `${r.w}-${r.l}${r.p ? `-${r.p}` : ""}`;
  return (
    <div className="space-y-5">
      {standalone && (
        <div className="fade-up">
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Likely To <span className="gold-text">Cash</span></h1>
        </div>
      )}
      {rec && (
        <div className="card-gold border border-[#a855f7]/40 rounded-xl p-3.5 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="cash-record">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Model Record — {rec.date} <span className="text-neutral-600">· resets daily</span></div>
            <div className="font-data text-lg font-black gold-text">{fmtRec(rec.overall)}{rec.overall.w + rec.overall.l > 0 ? ` (${Math.round((rec.overall.w / (rec.overall.w + rec.overall.l)) * 100)}%)` : ""}</div>
          </div>
          {MARKETS.map((m) => {
            const r = rec.markets?.[m.key];
            if (!r || r.w + r.l + r.p === 0) return null;
            return (
              <span key={m.key} className="text-xs font-data" data-testid={`cash-record-${m.key}`}>
                <span className="text-neutral-500 uppercase text-[10px] font-bold mr-1">{m.label}</span>
                <b className={r.w >= r.l ? "text-[#22c55e]" : "text-red-400"}>{fmtRec(r)}</b>
              </span>
            );
          })}
          <span className="text-[10px] text-neutral-600 ml-auto hidden sm:inline">Every pick auto-graded from final scores</span>
        </div>
      )}
      <p className="text-neutral-500 text-sm">
        {isH1
          ? <>First-half lines only — and only the ones the market prices at <span className="text-[#22c55e] font-bold">70%+ to hit</span>. 1H moneylines, spreads and totals across the slate, best price across the books. Tap any bet to read it in full.</>
          : <>The slate's most likely winners per market, ranked by devigged market probability. <span className="text-[#22c55e] font-bold">Lock = 80%+</span> · <span className="text-[#a855f7] font-bold">Pick = 70-79%</span> · under 70% is a <span className="text-neutral-400 font-bold">coin toss</span>. Tap any bet to read it in full.</>}
      </p>
      <div className="flex gap-2 overflow-x-auto" data-testid="cash-market-tabs">
        {tabs.map((m) => (
          <button key={m.key} onClick={() => { setMk(m.key); setOpen(null); }} data-testid={`cash-market-${m.key}`}
            className={`shrink-0 text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full border transition-colors duration-200 ${mk === m.key ? "bg-[#22c55e] text-black border-[#22c55e]" : "border-white/15 text-neutral-400 hover:border-[#22c55e]/60 hover:text-[#22c55e]"}`}>
            {m.label}
          </button>
        ))}
      </div>
      {loading && <div className="text-neutral-500 font-data text-sm animate-pulse">{isH1 ? "Pulling 1st-half lines from the books..." : "Ranking the slate..."}</div>}
      {!loading && bets.length === 0 && (
        <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="cash-empty">
          {isH1 ? "No 1st-half bets at 70%+ probability right now — books post 1H lines closer to game time." : `No ${MARKETS.find((m) => m.key === mk)?.label.toLowerCase()} posted for this slate yet — check back closer to game time.`}
        </div>
      )}
      <div className="space-y-2">
        {bets.map((b, i) => {
          const t = tier(b.prob);
          const isOpen = open === i;
          return (
            <div key={`${b.game}-${b.bet}`} role="button" aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : i)}
              className={`card-surface rounded-xl cursor-pointer transition-colors duration-200 hover:border-[#a855f7]/40 ${i === 0 ? "border border-[#22c55e]/40" : ""}`}
              data-testid={`cash-row-${i}`}>
              <div className="p-3.5 flex items-center gap-3">
                <span className={`font-data text-sm font-black w-6 text-center shrink-0 ${i < 3 ? "text-[#22c55e]" : "text-neutral-600"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase tracking-wide text-sm truncate">{b.bet}</div>
                  <div className="text-[11px] text-neutral-500 truncate">{mk !== "total" || isH1 ? `${b.game} · ` : ""}{b.day_label !== "Today" ? `${b.day_label} · ` : ""}{b.time_et}</div>
                </div>
                <div className="hidden sm:block w-32 shrink-0">
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#22c55e]" style={{ width: `${Math.min(b.prob, 100)}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-data text-sm font-black text-[#22c55e]">{b.prob}%</div>
                  <div className="text-[10px] font-data text-neutral-500">{fmtOdds(b.odds)}{b.book ? ` · ${b.book}` : ""}</div>
                </div>
                <span className={`text-[8px] uppercase tracking-widest font-black px-2 py-1 rounded-full border shrink-0 ${t.cls}`}>{t.label}</span>
                <ChevronDown size={14} className={`text-neutral-500 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </div>
              {isOpen && <BetDetail b={b} testid={`cash-detail-${i}`} />}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-neutral-600">Probabilities are market-implied (devigged), not guarantees — short odds mean small payouts. Great for parlay anchors; always compare the price at your book.</p>
    </div>
  );
}
